import type { NostrEvent } from '@nostrify/nostrify';
import { getTagValue, isValidWikiArticle } from '@/lib/nip54';
import { isValidPublicationIndex } from '@/lib/nkbip01';

/** localStorage key holding saved wiki articles. */
export const OFFLINE_LIBRARY_KEY = 'wikistar:offline-library';
/** localStorage key holding saved publications (index + sections). */
export const OFFLINE_PUBLICATIONS_KEY = 'wikistar:offline-publications';

/**
 * A minimal snapshot of the author's profile, captured at save time.
 *
 * Profile metadata (kind 0) normally comes from `useAuthor`, which
 * needs a relay — so without this an offline article would render with
 * a raw truncated pubkey instead of the author's name. Only the two
 * fields the article/card UI actually shows are kept, to avoid bloating
 * localStorage with full metadata for every saved article.
 */
export interface OfflineAuthor {
  name?: string;
  picture?: string;
}

export interface OfflineArticle {
  /** The complete signed event, exactly as received — so it stays independently verifiable offline. */
  event: NostrEvent;
  /** When the user saved it (ms since epoch), for "saved X ago" and newest-first ordering. */
  savedAt: number;
  author?: OfflineAuthor;
}

/**
 * A saved NKBIP-01 publication: the index event *plus* every section it
 * resolved to at save time.
 *
 * Unlike a wiki article, a publication isn't self-contained — the index
 * (kind 30040) holds only an ordered list of `a` tags, and the actual
 * prose lives in separate section events. Saving just the index would
 * produce a table of contents whose every entry fails to load offline,
 * so the whole set is captured together as one unit.
 */
export interface OfflinePublication {
  /** The publication index event (kind 30040). */
  index: NostrEvent;
  /**
   * The resolved section events, in the index's original display order.
   * Stored in order rather than re-resolved from the index's `a` tags at
   * read time, so the reading order survives offline exactly as saved.
   */
  sections: NostrEvent[];
  savedAt: number;
  author?: OfflineAuthor;
}

/**
 * Identifies a saved item by its addressable coordinate rather than its
 * event `id`. Wiki articles (NIP-54 kind 30818) and publication indices
 * (NKBIP-01 kind 30040) are both addressable events, so the *same* item
 * re-published as an edit gets a new `id` but keeps its `pubkey:d`
 * identity — keying on that means re-saving an edited item updates the
 * existing entry rather than accumulating a duplicate per revision.
 */
export function offlineKey(pubkey: string, slug: string): string {
  return `${pubkey}:${slug}`;
}

export function offlineKeyForEvent(event: NostrEvent): string | undefined {
  const slug = getTagValue(event, 'd');
  return slug ? offlineKey(event.pubkey, slug) : undefined;
}

export type OfflineLibrary = Record<string, OfflineArticle>;
export type OfflinePublicationLibrary = Record<string, OfflinePublication>;

export class OfflineQuotaError extends Error {
  constructor() {
    super('Not enough browser storage left to save this for offline reading.');
    this.name = 'OfflineQuotaError';
  }
}

/** Reads a raw JSON object from localStorage, tolerating unavailable/corrupt storage. */
function readStoredObject(key: string): Record<string, unknown> | undefined {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(key);
  } catch {
    // localStorage can throw outright (Safari private mode, disabled
    // storage) — an unavailable library is simply an empty one.
    return undefined;
  }
  if (!raw) return undefined;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return undefined;
    return parsed as Record<string, unknown>;
  } catch {
    return undefined;
  }
}

/** Persists a library, translating a full-storage failure into a typed error the UI can explain. */
function writeStoredObject(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // Most commonly QuotaExceededError. Either way the write failed, so
    // surface it rather than silently pretending the save succeeded.
    if (error instanceof DOMException) throw new OfflineQuotaError();
    throw error;
  }
}

function parseAuthor(value: unknown): OfflineAuthor | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const author = value as Partial<OfflineAuthor>;
  return {
    name: typeof author.name === 'string' ? author.name : undefined,
    picture: typeof author.picture === 'string' ? author.picture : undefined,
  };
}

/**
 * Reads and validates the stored articles.
 *
 * Everything here came from localStorage, which is fully
 * user/extension-writable and may also hold data from an older version
 * of the app — so each entry is re-validated as a real wiki article
 * (`isValidWikiArticle`) rather than trusted. A malformed entry is
 * dropped instead of being allowed to crash the reader or render as a
 * broken card.
 */
export function readOfflineLibrary(): OfflineLibrary {
  const parsed = readStoredObject(OFFLINE_LIBRARY_KEY);
  if (!parsed) return {};

  const out: OfflineLibrary = {};
  for (const [key, value] of Object.entries(parsed)) {
    if (!value || typeof value !== 'object') continue;
    const entry = value as Partial<OfflineArticle>;
    const event = entry.event;
    if (!event || typeof event !== 'object') continue;
    if (!isValidWikiArticle(event as NostrEvent)) continue;
    out[key] = {
      event: event as NostrEvent,
      savedAt: typeof entry.savedAt === 'number' ? entry.savedAt : 0,
      author: parseAuthor(entry.author),
    };
  }
  return out;
}

export function writeOfflineLibrary(library: OfflineLibrary): void {
  writeStoredObject(OFFLINE_LIBRARY_KEY, library);
}

/**
 * Reads and validates the stored publications.
 *
 * Same untrusted-input reasoning as `readOfflineLibrary`: the index is
 * re-validated as a real NKBIP-01 index, and each section is checked to
 * be a plausible addressable event with a `d` tag. Sections are *not*
 * validated with `isValidPublicationSection`, because an index may
 * legitimately reference other kinds as leaves (nested indices, wiki
 * articles, long-form content) — see `PUBLICATION_LEAF_KINDS`.
 */
export function readOfflinePublications(): OfflinePublicationLibrary {
  const parsed = readStoredObject(OFFLINE_PUBLICATIONS_KEY);
  if (!parsed) return {};

  const out: OfflinePublicationLibrary = {};
  for (const [key, value] of Object.entries(parsed)) {
    if (!value || typeof value !== 'object') continue;
    const entry = value as Partial<OfflinePublication>;
    const index = entry.index;
    if (!index || typeof index !== 'object') continue;
    if (!isValidPublicationIndex(index as NostrEvent)) continue;

    const sections = Array.isArray(entry.sections)
      ? entry.sections.filter(
          (section): section is NostrEvent =>
            Boolean(section) &&
            typeof section === 'object' &&
            typeof (section as NostrEvent).kind === 'number' &&
            typeof (section as NostrEvent).content === 'string' &&
            Array.isArray((section as NostrEvent).tags) &&
            Boolean(getTagValue(section as NostrEvent, 'd')),
        )
      : [];

    out[key] = {
      index: index as NostrEvent,
      sections,
      savedAt: typeof entry.savedAt === 'number' ? entry.savedAt : 0,
      author: parseAuthor(entry.author),
    };
  }
  return out;
}

export function writeOfflinePublications(library: OfflinePublicationLibrary): void {
  writeStoredObject(OFFLINE_PUBLICATIONS_KEY, library);
}

/** Saved articles, newest save first. */
export function sortOfflineArticles(library: OfflineLibrary): OfflineArticle[] {
  return Object.values(library).sort((a, b) => b.savedAt - a.savedAt);
}

/** Saved publications, newest save first. */
export function sortOfflinePublications(library: OfflinePublicationLibrary): OfflinePublication[] {
  return Object.values(library).sort((a, b) => b.savedAt - a.savedAt);
}
